import { yupResolver } from "@hookform/resolvers/yup";
import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import {
	Button,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Modalv2
} from "@itandi/itandi-bb-ui";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { NotificationModalForm } from "~/components/common/notifications/settings/NotificationModalForm";
import type { LineUserAccount } from "~/models/LineUserAccount";
import type {
	Notification,
	NotificationEdit,
	NotificationTargetType
} from "~/models/Notification";
import { buildInitialNotification, createSchema } from "~/models/Notification";
import type { Status } from "~/models/Status";
import type { Subtype } from "~/models/Subtype";
import { onInvalidScroll } from "~/utils/FormUtil";

type Props = DeepReadonly<{
	handleAcceptSubmit: (notification: NotificationEdit) => Promise<void>;
	handleClose: () => void;
	lineUserAccounts: LineUserAccount[] | null;
	notification?: Notification | null;
	statuses: Status[];
	subtypes: Subtype[] | null;
	type: NotificationTargetType;
	useLineNotification: boolean;
}>;

export const NotificationModal: React.FC<Props> = ({
	type,
	handleClose,
	statuses,
	subtypes,
	lineUserAccounts,
	useLineNotification,
	notification = null,
	handleAcceptSubmit
}: Props) => {
	const methods = useForm<NotificationEdit>({
		defaultValues: notification ?? buildInitialNotification(),
		mode: "onBlur",
		resolver: yupResolver(createSchema(type)),
		shouldFocusError: false
	});
	const handleSubmit = React.useCallback(() => {
		methods.handleSubmit(
			async () => {
				handleAcceptSubmit(methods.getValues()).then();
			},
			(errors) => {
				onInvalidScroll(errors);
			}
		)();
	}, [methods, handleAcceptSubmit]);
	return (
		<Modalv2 isOpen onClose={handleClose} size="l">
			<ModalHeader title="通知設定" />
			<ModalBody>
				<FormProvider {...methods}>
					<NotificationModalForm
						lineUserAccounts={lineUserAccounts}
						statuses={statuses}
						subtypes={subtypes}
						type={type}
						useLineNotification={useLineNotification}
					/>
				</FormProvider>
			</ModalBody>
			<ModalFooter
				leftArea={
					<Button onClick={handleClose} variant="secondary">
						キャンセル
					</Button>
				}
				rightArea={
					<Button onClick={handleSubmit} variant="primary">
						{notification ? "更新" : "追加"}
					</Button>
				}
			/>
		</Modalv2>
	);
};
